import React, { useMemo, useState } from "react"
import {
    useLocalParticipant,
} from "@livekit/components-react"
import { useSelector } from "react-redux"
import { usePermissions } from "@common/livekit/components/PermissionBanner"
import { useMicrophoneLevel } from "../../../engine/precall/useMicrophoneLevel"
import { useMicSilentDetection } from "@common/components/microphone/useMicSilentDetection"
import { PROBLEMS } from "@common/liveMode/TroubleshootingPanel"

/**
 * Shared hook for troubleshooting auto-detection logic.
 * Used by both StudentControlBar and LiveModeFooter.
 *
 * @param {Object} options
 * @param {boolean} options.isSoundMode - Whether the user is in dialogue mode (not concentrated)
 * @param {boolean} options.showTroubleshootingPanel - Whether the panel is currently open
 * @returns {{ micWarning, camWarning, detectedProblem, micLevel, permissionsGranted, requestPermissions, showHelpModal, setShowHelpModal, showTroubleshootingPanel, setShowTroubleshootingPanel, troubleshootInitialProblem, openTroubleshooting }}
 */
export const useTroubleshootingDetection = ({ isSoundMode }) => {
    const [showHelpModal, setShowHelpModal] = useState(false)
    const [showTroubleshootingPanel, setShowTroubleshootingPanel] = useState(false)
    const [troubleshootInitialProblem, setTroubleshootInitialProblem] = useState(null)

    const { isMicrophoneEnabled } = useLocalParticipant()
    // Use the debounced flag from Redux so the troubleshoot panel never
    // lights up before the banner — direct LiveKit quality is too twitchy.
    const isLocalPoorConnection = useSelector(
        (state) => state.liveMode?.isLocalPoorConnection || false
    )
    const { permissionsGranted, requestPermissions } = usePermissions()

    // Mic level detection — only active when troubleshooting panel is open
    // to avoid a permanent parallel audio stream alongside LiveKit
    const micLevelPermissions =
        showTroubleshootingPanel && isSoundMode !== false
            ? permissionsGranted
            : null
    const micLevel = useMicrophoneLevel(micLevelPermissions)

    // Detect OS-level mute (keyboard mute): mic should work but level stays at 0
    const micShouldWork =
        permissionsGranted?.microphone &&
        isMicrophoneEnabled !== false &&
        isSoundMode !== false
    const isMicSilent = useMicSilentDetection(
        micLevel,
        showTroubleshootingPanel && micShouldWork
    )

    const micWarning = !permissionsGranted?.microphone || isMicSilent
    const camWarning = !permissionsGranted?.camera

    const detectedProblem = useMemo(() => {
        if (isLocalPoorConnection) {
            return PROBLEMS.CONNECTION_ISSUES
        }
        if (!permissionsGranted?.microphone || isMicSilent) {
            return PROBLEMS.MIC_NOT_WORKING
        }
        if (!permissionsGranted?.camera) {
            return PROBLEMS.CAMERA_NOT_WORKING
        }
        return null
    }, [isLocalPoorConnection, permissionsGranted, isMicSilent])

    const openTroubleshooting = (problem = null) => {
        setTroubleshootInitialProblem(problem)
        setShowTroubleshootingPanel(true)
    }

    return {
        micWarning,
        camWarning,
        detectedProblem,
        micLevel,
        permissionsGranted,
        requestPermissions,
        showHelpModal,
        setShowHelpModal,
        showTroubleshootingPanel,
        setShowTroubleshootingPanel,
        troubleshootInitialProblem,
        openTroubleshooting,
    }
}
